import React from 'react';
import { GripVertical, Edit, Trash2, Check, Copy } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../ui/button';

export const SortableMenuCard = ({ item, onEdit, onDelete, onDuplicate, selectionMode, isSelected, onToggleSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const handleCardClick = (e) => {
    if (selectionMode) {
      e.preventDefault();
      onToggleSelect(item.id);
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`bg-white rounded-2xl overflow-hidden shadow-sm p-6 relative ${
        selectionMode ? 'cursor-pointer hover:ring-2 hover:ring-primary/30' : ''
      } ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={handleCardClick}
    >
      {/* Selection checkbox */}
      {selectionMode && (
        <div 
          className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
            isSelected ? 'bg-primary border-primary' : 'border-gray-300 bg-white'
          }`}
        >
          {isSelected && <Check className="h-4 w-4 text-white" />}
        </div>
      )}

      <div className="flex items-start gap-2 mb-2">
        {!selectionMode && (
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded touch-none mt-1"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-gray-400" />
          </button>
        )}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-8">
              <h3 className="font-bold text-lg line-clamp-1">{item.name}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
            </div>
            {!item.available && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full ml-2 absolute top-4 right-12">
                Unavailable
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={`flex items-center justify-between mt-3 ${selectionMode ? '' : 'pl-8'}`}>
        <span className="text-xl font-bold text-primary">£{item.price.toFixed(2)}</span>
        {!selectionMode && (
          <div className="flex gap-2">
            <Button
              data-testid="edit-item-button"
              variant="outline"
              size="icon"
              onClick={() => onEdit(item)}
              className="h-8 w-8"
              title="Edit item"
            >
              <Edit className="h-4 w-4" />
            </Button>
            {onDuplicate && (
              <Button
                data-testid="duplicate-item-button"
                variant="outline"
                size="icon"
                onClick={() => onDuplicate(item)}
                className="h-8 w-8 text-blue-600 hover:text-blue-700"
                title="Duplicate item"
              >
                <Copy className="h-4 w-4" />
              </Button>
            )}
            <Button
              data-testid="delete-item-button"
              variant="outline"
              size="icon"
              onClick={() => onDelete(item.id)}
              className="h-8 w-8 text-red-600 hover:text-red-700"
              title="Delete item"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      <div className={`mt-2 ${selectionMode ? '' : 'pl-8'}`}>
        <span className="text-xs text-muted-foreground">
          {item.category}{item.subcategory ? ` > ${item.subcategory}` : ''}
        </span>
      </div>
    </div>
  );
};
